/**
 * Phase 2 — Flight sync service.
 *
 * Responsible for bi-directional sync between the local device
 * storage (CSV/Filesystem) and the remote server.
 *
 * Upload uses compact binary track encoding (~90% smaller than
 * base64 CSV) with gzip transport compression.
 *
 * Sync strategy (to be refined in phase 2):
 *  - On login:  pull all remote flights that don't exist locally
 *  - On save:   mark flight as "modified", queue for upload
 *  - On sync:   push "local"/"modified" flights, pull new remote ones
 *  - Conflict:  last-writer-wins (or prompt user — TBD)
 */

import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Media } from '@capacitor-community/media';
import { api, fetchAuthenticated, getTokens, isAuthenticated, getApiBaseUrl } from './api';
import {
  captureFailure,
  log,
  warn,
} from '@/lib/appTelemetry';
import type {
  RemoteFlight,
  UploadFlightPayload,
  ApiResponse,
  TrackDownloadResponse,
} from '@/types/api';
import type { Flight, FlightMeta, FlightPoint } from '@/types/flight';
import { csvToPoints } from '@/lib/trackCsv';
import {
  encodeTrackBinary,
  decodeTrackBinary,
  arrayBufferToBase64,
  base64ToArrayBuffer,
} from '@/utils/trackBinary';

/** Derive required metadata from points when meta is incomplete (e.g. corrupted index) */
function maxKnownAltitude(points: FlightPoint[]): number {
  let max = 0;
  for (const point of points) {
    if (point.altitude != null && Number.isFinite(point.altitude)) {
      max = Math.max(max, point.altitude);
    }
  }
  return max;
}

function computeMetadataFromPoints(points: FlightPoint[]): {
  startTime: number;
  endTime: number;
  duration: number;
  maxAltitude: number;
  distance: number;
} {
  if (points.length < 2) {
    const ts = points[0]?.timestamp ?? Date.now();
    return {
      startTime: ts,
      endTime: ts,
      duration: 0,
      maxAltitude: maxKnownAltitude(points),
      distance: 0,
    };
  }
  const startTime = points[0].timestamp;
  const endTime = points[points.length - 1].timestamp;
  const maxAltitude = maxKnownAltitude(points);
  let distance = 0;
  const R = 6371; // Earth radius km
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  for (let i = 1; i < points.length; i++) {
    const p1 = points[i - 1];
    const p2 = points[i];
    const dLat = toRad(p2.latitude - p1.latitude);
    const dLon = toRad(p2.longitude - p1.longitude);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(p1.latitude)) *
        Math.cos(toRad(p2.latitude)) *
        Math.sin(dLon / 2) ** 2;
    distance += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  return {
    startTime,
    endTime,
    duration: endTime - startTime,
    maxAltitude,
    distance,
  };
}

/**
 * Build a complete upload payload, filling any missing meta fields from points.
 * This makes sync resilient to corrupted or incomplete index data.
 */
function buildUploadPayload(
  meta: FlightMeta,
  points: FlightPoint[],
  trackDataBase64: string,
): UploadFlightPayload {
  const computed = computeMetadataFromPoints(points);
  const payload: UploadFlightPayload = {
    name: meta.name ?? `Flight ${new Date(computed.startTime).toLocaleString()}`,
    startTime: meta.startTime ?? computed.startTime,
    endTime: meta.endTime ?? computed.endTime,
    distance: meta.distance ?? computed.distance,
    maxAltitude: meta.maxAltitude ?? computed.maxAltitude,
    duration: meta.duration ?? computed.duration,
    trackData: trackDataBase64,
  };
  if (meta.hobbsTime != null) payload.hobbsTime = meta.hobbsTime;
  if (meta.fuelLevel != null) payload.fuelLevel = meta.fuelLevel;
  if (meta.notes != null) payload.notes = meta.notes;
  if (meta.site != null) payload.site = meta.site;
  if (meta.aircraft != null) payload.aircraft = meta.aircraft;
  return payload;
}

/**
 * Upload a single flight to the server using compact binary track encoding.
 *
 * Flow:
 *  1. Encode FlightPoint[] → binary ArrayBuffer (20 bytes/point)
 *  2. Base64-encode the binary data
 *  3. Send as JSON with `trackData` field
 *  4. The API client gzip-compresses the request body automatically
 *
 * Missing meta fields are derived from points to handle corrupted index data.
 */
export async function uploadFlight(
  meta: FlightMeta,
  points: FlightPoint[],
): Promise<ApiResponse<RemoteFlight>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }

  // Encode points to compact binary and then to base64
  const binaryBuffer = encodeTrackBinary(points);
  const trackDataBase64 = arrayBufferToBase64(binaryBuffer);

  const payload = buildUploadPayload(meta, points, trackDataBase64);

  const result = await api.post<RemoteFlight>('/flights', payload);
  if (result.success && result.data) {
    log('sync', 'upload ok', { localId: meta.id, remoteId: result.data.id, points: points.length });
  } else {
    captureFailure('sync', 'upload failed', undefined, {
      localId: meta.id,
      code: result.error?.code,
      message: result.error?.message,
    });
  }
  return result;
}

/**
 * Upload a single flight using legacy CSV format (fallback).
 */
export async function uploadFlightCsv(
  meta: FlightMeta,
  trackCsv: string,
): Promise<ApiResponse<RemoteFlight>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }

  const payload: UploadFlightPayload = {
    name: meta.name,
    startTime: meta.startTime,
    endTime: meta.endTime,
    distance: meta.distance,
    maxAltitude: meta.maxAltitude,
    duration: meta.duration,
    trackCsv: btoa(trackCsv), // base-64 encode the CSV
  };
  if (meta.hobbsTime != null) payload.hobbsTime = meta.hobbsTime;
  if (meta.fuelLevel != null) payload.fuelLevel = meta.fuelLevel;
  if (meta.notes != null) payload.notes = meta.notes;
  if (meta.site != null) payload.site = meta.site;
  if (meta.aircraft != null) payload.aircraft = meta.aircraft;

  return api.post<RemoteFlight>('/flights', payload);
}

/** Import a GPX/KML file (backend parses and normalizes). */
export async function importFlightFile(
  file: File,
  options: {
    name?: string;
    hobbsTime?: string | null;
    fuelLevel?: string | null;
    notes?: string | null;
    site?: string | null;
    aircraft?: string | null;
  } = {},
): Promise<ApiResponse<RemoteFlight>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }

  const formData = new FormData();
  formData.append('trackFile', file);
  if (options.name?.trim()) formData.append('name', options.name.trim());
  if (options.hobbsTime != null) formData.append('hobbsTime', options.hobbsTime);
  if (options.fuelLevel != null) formData.append('fuelLevel', options.fuelLevel);
  if (options.notes != null) formData.append('notes', options.notes);
  if (options.site != null) formData.append('site', options.site);
  if (options.aircraft != null) formData.append('aircraft', options.aircraft);

  return api.postForm<RemoteFlight>('/flights/import', formData);
}

/** GeoJSON FeatureCollection for flight track (RFC 7946) */
export interface FlightGeoJSON {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: { type: 'LineString'; coordinates: [number, number, number][] };
    properties?: Record<string, unknown>;
  }>;
}

/** Fetch flight track as GeoJSON from the API (for synced flights with remoteId). */
export async function fetchFlightGeoJson(remoteId: string): Promise<FlightGeoJSON | null> {
  if (!isAuthenticated()) return null;
  try {
    const BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
    const token = getTokens()?.accessToken;
    const res = await fetch(`${BASE}/flights/${encodeURIComponent(remoteId)}/geojson`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return null;
    return (await res.json()) as FlightGeoJSON;
  } catch {
    return null;
  }
}

/** Fetch the list of flights from the server */
export async function fetchRemoteFlights(): Promise<ApiResponse<RemoteFlight[]>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }
  return api.get<RemoteFlight[]>('/flights');
}

/**
 * Download the track for a remote flight as FlightPoint[].
 *
 * Requests the binary format from the API and decodes it.
 * Falls back to CSV parsing if the response is not in binary format.
 */
export async function downloadTrack(trackUrl: string): Promise<FlightPoint[]> {
  try {
    // Request binary format
    const binaryUrl = trackUrl.includes('?')
      ? `${trackUrl}&format=binary`
      : `${trackUrl}?format=binary`;

    const res = await api.get<TrackDownloadResponse>(
      binaryUrl.replace(/^.*\/api/, ''),
    );

    if (res.success && res.data?.trackData) {
      const buffer = base64ToArrayBuffer(res.data.trackData);
      const points = decodeTrackBinary(buffer);
      log('sync', 'download track ok', { points: points.length, format: 'binary' });
      return points;
    }

    // Fallback: try CSV download (use full API URL; trackUrl may be relative).
    // The track route is behind auth:sanctum, so this must carry the bearer token,
    // and it has to ask for CSV explicitly — the endpoint defaults to binary JSON.
    const path = trackUrl.replace(/^\/?api\/?/, '');
    const base = /^https?:\/\//i.test(trackUrl)
      ? trackUrl
      : path
        ? `${getApiBaseUrl().replace(/\/$/, '')}/${path.replace(/^\//, '')}`
        : getApiBaseUrl();
    const fullUrl = base.includes('?') ? `${base}&format=csv` : `${base}?format=csv`;
    const csvRes = await fetchAuthenticated(fullUrl);
    if (!csvRes.ok) throw new Error(`Failed to download track: ${csvRes.status}`);
    const csv = await csvRes.text();
    const points = csvToPoints(csv);
    log('sync', 'download track ok', { points: points.length, format: 'csv' });
    return points;
  } catch (err) {
    captureFailure('sync', 'download track failed', err);
    throw err;
  }
}

/** Update a flight's name on the server */
export async function updateRemoteFlightName(
  remoteId: string,
  name: string,
): Promise<ApiResponse<RemoteFlight>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }
  return api.patch<RemoteFlight>(`/flights/${remoteId}`, { name });
}

/** Logbook fields that can be PATCHed on the server */
export interface RemoteFlightLogbookUpdate {
  hobbsTime?: string | null;
  fuelLevel?: string | null;
  notes?: string | null;
  site?: string | null;
  aircraft?: string | null;
}

/** Update a flight's logbook fields on the server */
export async function updateRemoteFlightLogbook(
  remoteId: string,
  logbook: RemoteFlightLogbookUpdate,
): Promise<ApiResponse<RemoteFlight>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }
  return api.patch<RemoteFlight>(`/flights/${remoteId}`, logbook);
}

/** Enable sharing for a flight; returns updated flight with shareUrl. */
export async function enableFlightSharing(remoteId: string): Promise<ApiResponse<RemoteFlight>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }
  return api.patch<RemoteFlight>(`/flights/${remoteId}`, { shareEnabled: true });
}

/**
 * Open the system share sheet with the flight's public share page URL.
 * On native: uses Capacitor Share. On web: copies only the URL to clipboard (no title/text) so pasted links work.
 */
export async function shareFlightPage(shareUrl: string, title: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await Share.share({
      title: title || 'My flight',
      text: title ? `${title} — Flight Track` : 'Flight Track',
      url: shareUrl,
      dialogTitle: 'Share flight',
    });
    return;
  }
  const urlOnly = shareUrl.trim().split(/[\r\n]+/)[0]?.trim() ?? shareUrl.trim();
  if (!urlOnly) return;
  if (typeof window !== 'undefined') window.getSelection()?.removeAllRanges();
  try {
    await navigator.clipboard.write([
      new ClipboardItem({ 'text/plain': new Blob([urlOnly], { type: 'text/plain' }) }),
    ]);
  } catch {
    await navigator.clipboard.writeText(urlOnly);
  }
  // Caller shows "Link copied to clipboard" when !Capacitor.isNativePlatform()
}

/** Fetch a single flight from the server (for polling replay status). */
export async function fetchRemoteFlight(
  remoteId: string,
): Promise<ApiResponse<RemoteFlight>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }
  return api.get<RemoteFlight>(`/flights/${remoteId}`);
}

const DEFAULT_POLL_INTERVAL_MS = 8000;
const MAX_POLL_ATTEMPTS = 120; // ~16 min at 8s

/** True when replay generation was accepted but the video is not ready yet. */
export function isReplayGenerationInProgress(status: string | undefined): boolean {
  return status === 'queued' || status === 'generating';
}

export type ReplayStatusPayload = {
  status: string;
  progress?: number | null;
  stage?: string | null;
  replayUrl?: string | null;
};

/** Poll replay generation status for a remote flight. */
export async function fetchReplayStatus(
  remoteId: string,
): Promise<ApiResponse<ReplayStatusPayload>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }
  return api.get<ReplayStatusPayload>(`/flights/${remoteId}/replay/status`);
}

/** Poll flight until replay is ready, then call onReady. Returns a stop function. */
export function pollReplayReady(
  remoteId: string,
  onReady: (replayUrl: string) => void,
  options: {
    pollIntervalMs?: number;
    maxAttempts?: number;
    onExhausted?: () => void;
  } = {},
): () => void {
  const intervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const maxAttempts = options.maxAttempts ?? MAX_POLL_ATTEMPTS;
  let attempts = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  function stopTimers(): void {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  }

  function tick() {
    if (stopped) return;
    attempts += 1;
    if (attempts > maxAttempts) {
      stopTimers();
      options.onExhausted?.();
      return;
    }
    fetchRemoteFlight(remoteId)
      .then((res) => {
        if (stopped) return;
        if (res.success && res.data?.replayUrl) {
          stopTimers();
          onReady(res.data.replayUrl);
          return;
        }
        timeoutId = setTimeout(tick, intervalMs);
      })
      .catch(() => {
        if (!stopped) timeoutId = setTimeout(tick, intervalMs);
      });
  }

  tick();

  return function stop() {
    stopped = true;
    stopTimers();
  };
}

/** Map settings to use when generating the replay video (optional). */
export interface ReplayMapSettings {
  basemapId: string;
  colourSchemeId: string;
  cameraFollowHeading: boolean;
  showPath3D: boolean;
}

/** Partial map settings for updating only changed fields. */
export type ReplayMapSettingsPatch = Partial<ReplayMapSettings>;

/** Persist replay map settings for a flight. Send only the keys that changed; backend merges with existing. */
export async function updateReplayMapSettings(
  remoteId: string,
  patch: ReplayMapSettingsPatch,
): Promise<ApiResponse<null>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }
  const body: { map_settings: Record<string, unknown> } = { map_settings: {} };
  if (patch.basemapId !== undefined) body.map_settings.basemapId = patch.basemapId;
  if (patch.colourSchemeId !== undefined) body.map_settings.colourSchemeId = patch.colourSchemeId;
  if (patch.cameraFollowHeading !== undefined) body.map_settings.cameraFollowHeading = patch.cameraFollowHeading;
  if (patch.showPath3D !== undefined) body.map_settings.showPath3D = patch.showPath3D;
  if (Object.keys(body.map_settings).length === 0) return { success: true, data: null };
  return api.patch<null>(`/flights/${remoteId}/replay-map-settings`, body);
}

/** Trigger replay video generation for a remote flight. Returns status: 'ready' | 'generating'. Optionally pass mapSettings to persist for this flight's replay. */
export async function generateReplayVideo(
  remoteId: string,
  mapSettings?: ReplayMapSettings | null,
): Promise<ApiResponse<{ status: string; replayUrl?: string; message?: string }>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }
  const body = mapSettings
    ? { map_settings: { basemapId: mapSettings.basemapId, colourSchemeId: mapSettings.colourSchemeId, cameraFollowHeading: mapSettings.cameraFollowHeading, showPath3D: mapSettings.showPath3D } }
    : undefined;
  log('replay', 'generate requested', { remoteId });
  const result = await api.post<{ status: string; replayUrl?: string; message?: string }>(
    `/flights/${remoteId}/replay/generate`,
    body,
  );
  if (!result.success) {
    captureFailure('replay', 'generate failed', undefined, {
      remoteId,
      code: result.error?.code,
      message: result.error?.message,
    });
  } else {
    log('replay', 'generate accepted', { remoteId, status: result.data?.status });
  }
  return result;
}

/** Unread notification from the API (e.g. replay_ready). */
export interface AppNotification {
  id: string;
  type: string | null;
  data: { type?: string; flight_id?: string; flight_name?: string; message?: string };
  created_at?: string;
}

/** Fetch unread notifications (for replay ready, etc.). */
export async function fetchNotifications(): Promise<ApiResponse<{ notifications: AppNotification[] }>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }
  return api.get<{ notifications: AppNotification[] }>('/notifications');
}

/** Mark a notification as read. */
export async function markNotificationRead(id: string): Promise<ApiResponse<{ marked: boolean }>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }
  return api.patch<{ marked: boolean }>(`/notifications/${id}/read`);
}

/** Register FCM device token for push notifications (Android). */
export async function registerDeviceToken(
  token: string,
  platform: 'android' | 'ios' = 'android',
): Promise<ApiResponse<{ id: string }>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }
  return api.post<{ id: string }>('/device-token', { token, platform });
}

function replayFileName(flightName: string | undefined, ext: string): string {
  const sanitized = flightName ? flightName.replace(/[^a-zA-Z0-9_-]/g, '') : '';
  const baseName = sanitized ? `flight-replay-${sanitized}-${Date.now()}` : `flight-replay-${Date.now()}`;
  return `${baseName}.${ext}`;
}

/** Trigger a browser download/save for a video blob (File System Access API when available). */
async function saveReplayBlobOnWeb(blob: Blob, filename: string, ext: string): Promise<void> {
  const picker = (window as Window & { showSaveFilePicker?: (options: unknown) => Promise<FileSystemFileHandle> })
    .showSaveFilePicker;
  if (typeof picker === 'function') {
    try {
      const handle = await picker({
        suggestedName: filename,
        types: [{ description: 'Video', accept: { [`video/${ext}`]: [`.${ext}`] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
    }
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/** Download replay video (fetches with auth; on native saves to gallery, on web triggers browser download) */
export async function downloadReplayVideo(replayUrl: string, flightName?: string): Promise<void> {
  const res = await fetchAuthenticated(replayUrl);
  if (!res.ok) {
    throw new Error(`Failed to download replay (${res.status})`);
  }
  const blob = await res.blob();
  const contentType = res.headers.get('content-type') ?? '';
  const ext = contentType.includes('webm') ? 'webm' : 'mp4';
  const filename = replayFileName(flightName, ext);

  if (Capacitor.isNativePlatform()) {
    try {
      await Media.createAlbum({ name: 'Flight Track' });
    } catch {
      /* album may already exist */
    }
    const { albums } = await Media.getAlbums();
    const albumsPath =
      Capacitor.getPlatform() === 'android' ? (await Media.getAlbumsPath()).path : undefined;
    const flightAlbum =
      Capacitor.getPlatform() === 'android' && albumsPath
        ? albums.find(
            (a: { name: string; identifier: string }) =>
              a.name === 'Flight Track' && a.identifier.startsWith(albumsPath),
          )
        : albums?.find((a: { name: string }) => a.name === 'Flight Track');
    if (!flightAlbum?.identifier) {
      throw new Error('Flight Track album not found; try creating it first');
    }

    const base64 = await blobToBase64(blob);
    const cachePath = `replay-${Date.now()}.${ext}`;
    const { uri } = await Filesystem.writeFile({
      path: cachePath,
      data: base64.split(',')[1] ?? base64,
      directory: Directory.Cache,
    });
    try {
      await Media.saveVideo({
        path: uri,
        albumIdentifier: flightAlbum.identifier,
        fileName: filename.replace(`.${ext}`, ''),
      });
    } finally {
      await Filesystem.deleteFile({ path: cachePath, directory: Directory.Cache }).catch(() => {});
    }
  } else {
    await saveReplayBlobOnWeb(blob, filename, ext);
  }
}

/** Share replay video (native only: fetches with auth, writes to cache, opens system share sheet). No-op on web. */
export async function shareReplayVideo(replayUrl: string, flightName?: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const res = await fetchAuthenticated(replayUrl);
  if (!res.ok) {
    throw new Error(`Failed to download replay (${res.status})`);
  }
  const blob = await res.blob();
  const contentType = res.headers.get('content-type') ?? '';
  const ext = contentType.includes('webm') ? 'webm' : 'mp4';
  const filename = replayFileName(flightName, ext);

  const base64 = await blobToBase64(blob);
  const cachePath = `replay-share-${Date.now()}.${ext}`;
  const { uri } = await Filesystem.writeFile({
    path: cachePath,
    data: base64.split(',')[1] ?? base64,
    directory: Directory.Cache,
  });
  try {
    await Share.share({
      title: 'Flight Replay',
      text: flightName ? `Replay: ${flightName}` : 'Flight replay video',
      url: uri,
      dialogTitle: 'Share replay',
    });
  } finally {
    // Allow share target to read file before deleting (Android/iOS may copy on share)
    setTimeout(() => {
      Filesystem.deleteFile({ path: cachePath, directory: Directory.Cache }).catch(() => {});
    }, 5000);
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Delete a flight on the server */
export async function deleteRemoteFlight(
  remoteId: string,
): Promise<ApiResponse<void>> {
  if (!isAuthenticated()) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }
  const result = await api.delete<void>(`/flights/${remoteId}`);
  if (result.success) {
    log('sync', 'delete ok', { remoteId });
  } else {
    captureFailure('sync', 'delete failed', undefined, {
      remoteId,
      code: result.error?.code,
      message: result.error?.message,
    });
  }
  return result;
}

export interface SyncResult {
  /** Local flight ids that were successfully uploaded, with their new remote ids */
  pushed: { localId: string; remoteId: string }[];
  /** Remote flights that exist on the server but not locally */
  pulled: RemoteFlight[];
}

/**
 * Full bi-directional sync.
 * Uploads local/modified flights, returns what was pushed and what can be pulled.
 */
export async function syncAll(
  localFlights: FlightMeta[],
  getFlightWithPoints: (id: string) => Promise<Flight | undefined>,
): Promise<SyncResult> {
  const pushed: { localId: string; remoteId: string }[] = [];
  const pulled: RemoteFlight[] = [];

  if (!isAuthenticated()) {
    warn('sync', 'skip — not authenticated');
    return { pushed, pulled };
  }

  log('sync', 'syncAll start', { localCount: localFlights.length });

  try {
    // 1. Fetch remote flight list
    const remoteResult = await fetchRemoteFlights();
    if (!remoteResult.success || !remoteResult.data) {
      captureFailure('sync', 'fetch remote flights failed', undefined, {
        code: remoteResult.error?.code,
        message: remoteResult.error?.message,
      });
      return { pushed, pulled };
    }

    const remoteFlights = remoteResult.data;
    const remoteIds = new Set(remoteFlights.map((f) => f.id));

    // 2. Upload local-only flights
    const localOnly = localFlights.filter(
      (f) => f.syncStatus === 'local' || f.syncStatus === 'modified',
    );

    for (const meta of localOnly) {
      // Skip if already synced (has a remoteId that still exists)
      if (meta.remoteId && remoteIds.has(meta.remoteId)) {
        continue;
      }

      const flight = await getFlightWithPoints(meta.id);
      if (!flight || flight.points.length === 0) {
        warn('sync', 'skip flight — no points', { localId: meta.id });
        continue;
      }

      const result = await uploadFlight(meta, flight.points);
      if (result.success && result.data) {
        pushed.push({ localId: meta.id, remoteId: result.data.id });
      }
    }

    // 3. Identify remote-only flights for pulling
    const localRemoteIds = new Set(
      localFlights.filter((f) => f.remoteId).map((f) => f.remoteId!),
    );

    for (const remote of remoteFlights) {
      if (!localRemoteIds.has(remote.id)) {
        pulled.push(remote);
      }
    }
  } catch (err) {
    captureFailure('sync', 'syncAll error', err);
  }

  log('sync', 'syncAll done', { pushed: pushed.length, pulled: pulled.length });
  return { pushed, pulled };
}
